import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, TextField, Button, TablePagination
} from '@mui/material';
import axios from 'axios';

const RawContactEditor = () => {
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);        // zero-based
  const [pageSize, setPageSize] = useState(50);
  const [rowCount, setRowCount] = useState(0);
  const [massEditData, setMassEditData] = useState({ commonComment: '' });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', commonComment: '' });

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/rawcontacts?page=${page + 1}&limit=${pageSize}`, {
        withCredentials: true,
      });
      setRows(res.data.data);
      setRowCount(res.data.total);
      setSelectedRows([]); // clear selection on page change
    } catch (err) {
      console.error('Failed to fetch data', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(rows.map(row => row._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const handleMassEdit = async () => {
    if (selectedRows.length === 0 || !massEditData.commonComment.trim()) return;
    try {
      await axios.post(`${API_BASE_URL}/api/rawcontacts/mass-edit`, {
        ids: selectedRows,
        updateData: massEditData,
      }, { withCredentials: true });
      setMassEditData({ commonComment: '' });
      fetchData();
    } catch (err) {
      console.error('Mass edit failed', err);
    }
  };

  const startEditing = (row) => {
    setEditingRowId(row._id);
    setEditFormData({ name: row.name, commonComment: row.commonComment || '' });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditFormData({ name: '', commonComment: '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/api/rawcontacts/mass-edit`, {
        ids: [id],
        updateData: editFormData,
      }, { withCredentials: true });
      setEditingRowId(null);
      fetchData();
    } catch (err) {
      console.error('Row update failed', err);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                  checked={rows.length > 0 && selectedRows.length === rows.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const isSelected = selectedRows.includes(row._id);
              const isEditing = editingRowId === row._id;

              return (
                <TableRow key={row._id} hover selected={isSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectRow(row._id)}
                    />
                  </TableCell>

                  <TableCell>{row._id}</TableCell>

                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        size="small"
                      />
                    ) : (
                      row.name
                    )}
                  </TableCell>

                  <TableCell>{row.phone}</TableCell>
                  <TableCell>{row.email}</TableCell>

                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={editFormData.commonComment}
                        onChange={(e) => setEditFormData({ ...editFormData, commonComment: e.target.value })}
                        size="small"
                      />
                    ) : (
                      row.commonComment
                    )}
                  </TableCell>

                  <TableCell>
                    {isEditing ? (
                      <>
                        <Button size="small" variant="contained" onClick={() => saveEdit(row._id)}>Save</Button>
                        <Button size="small" onClick={cancelEditing}>Cancel</Button>
                      </>
                    ) : (
                      <Button size="small" onClick={() => startEditing(row)}>Edit</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Mass Edit Section */}
      <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
        <TextField
          label="Mass Edit Comment"
          value={massEditData.commonComment}
          onChange={(e) => setMassEditData({ commonComment: e.target.value })}
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleMassEdit}
          disabled={selectedRows.length === 0 || !massEditData.commonComment.trim()}
        >
          Mass Edit Selected
        </Button>
      </div>
    </div>
  );
};

export default RawContactEditor;
